import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import CT from '@marketplace-ai/tags';
import { FormEvent, useId, useState } from 'react';
import { Button } from '~/components/buttons/Button/Button';

const DETAIL_TAGS = CT.employer.contracts.contractDetail;

// [MCV-1829] The server stores the version of this wording with the signature
// (CONSENT_TEXT_VERSION "contract-esign-1"); change both together.
export const SIGN_CONSENT_TEXT =
  'By typing your name and selecting Sign Contract, I agree to sign this contract electronically. My typed name is my signature and has the same legal effect as a handwritten signature.';

// Mirrors the server's minimum so the button stays off for a name it would reject.
const MIN_SIGNER_NAME_LENGTH = 2;

interface Props {
  open: boolean;
  // What the person is signing, e.g. the listing title or the contract number.
  contractLabel: string;
  // True while the sign request is in flight, so a double click cannot post twice.
  signing?: boolean;
  // The API's 400 detail from the last attempt (a name or consent it rejected).
  error?: string | null;
  onClose: () => void;
  onConfirm: (signerName: string, consentAccepted: boolean) => void;
}

export const SignContractDialog = ({
  open,
  contractLabel,
  signing = false,
  error = null,
  onClose,
  onConfirm,
}: Props) => {
  const [signerName, setSignerName] = useState('');
  const [consented, setConsented] = useState(false);
  // The error belongs to the last attempt. Editing the form hides it until the
  // next attempt, and a freshly opened dialog never shows an earlier one.
  const [attempted, setAttempted] = useState(false);
  const nameId = useId();
  const consentId = useId();

  const trimmedName = signerName.trim();
  const canSubmit = trimmedName.length >= MIN_SIGNER_NAME_LENGTH && consented && !signing;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setAttempted(true);
    onConfirm(trimmedName, consented);
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-grey-100/60 backdrop-blur-sm transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
        <DialogPanel
          transition
          className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full max-h-[calc(100dvh-2rem)] overflow-y-auto max-sm:[overflow-wrap:anywhere] text-left transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:data-closed:translate-y-0 sm:data-closed:scale-95"
          data-cy={DETAIL_TAGS.signDialog}
        >
          <DialogTitle className="text-lg font-semibold text-grey-100 mb-2">
            Sign contract
          </DialogTitle>
          <p className="text-sm text-grey-500 mb-4">{`You are signing ${contractLabel}.`}</p>
          <form onSubmit={handleSubmit} noValidate>
            <label htmlFor={nameId} className="block text-sm font-medium text-grey-100 mb-1">
              Type your full legal name
            </label>
            <input
              id={nameId}
              type="text"
              value={signerName}
              maxLength={200}
              autoComplete="off"
              onChange={(e) => {
                setSignerName(e.target.value);
                setAttempted(false);
              }}
              className="block w-full border border-grey-800 rounded-md p-3 text-sm mb-4 transition-colors duration-150 focus:border-primary-50 focus:ring-3 focus:ring-primary-50/12 focus:outline-none placeholder:text-grey-600"
              data-cy={DETAIL_TAGS.signerName}
            />
            <label htmlFor={consentId} className="flex items-start gap-3 text-sm mb-4">
              <input
                id={consentId}
                type="checkbox"
                checked={consented}
                onChange={(e) => {
                  setConsented(e.target.checked);
                  setAttempted(false);
                }}
                className="mt-0.5 size-5 shrink-0 cursor-pointer rounded-md bg-grey-900 border border-grey-800 accent-primary-50 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary-50"
                data-cy={DETAIL_TAGS.signConsent}
              />
              <span className="text-grey-100">{SIGN_CONSENT_TEXT}</span>
            </label>
            {attempted && error && (
              <p role="alert" className="text-sm text-error mb-4" data-cy={DETAIL_TAGS.signError}>
                {error}
              </p>
            )}
            <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
              <Button
                wrap
                colorVariant="secondary"
                onClick={onClose}
                label="Cancel"
                type="button"
                cyTag={DETAIL_TAGS.signCancel}
              />
              <Button
                wrap
                colorVariant="primary"
                label="Sign Contract"
                type="submit"
                disabled={!canSubmit}
                cyTag={DETAIL_TAGS.signConfirm}
              />
            </div>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
